import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Servizi', path: '/servizi' },
    { name: 'Prodotti', path: '/prodotti' },
    { name: 'Come Lavoriamo', path: '/come-lavoriamo' },
    { name: 'Galleria', path: '/galleria' },
    { name: 'Contatti', path: '/contatti' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="header-sisaline">
      <nav className="nav-container">
        <div className="nav-content container">
          {/* Logo */}
          <Link to="/" className="logo-link" onClick={() => window.scrollTo(0, 0)}>
            <img 
              src="https://customer-assets.emergentagent.com/job_1339b4ee-79eb-48d1-9f64-307ac7401951/artifacts/shomcg6m_Logo3.jpg" 
              alt="SI.SA. LINE S.r.l." 
              className="logo-image"
            />
            <div className="logo-text">
              <span className="logo-name">SI.SA. LINE <span className="logo-srl">S.r.l.</span></span>
              <span className="logo-tagline">Segnaletica Stradale</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="nav-links-desktop">
            {navigation.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-link ${isActive(item.path) ? 'nav-link-active' : ''}`}
                onClick={() => window.scrollTo(0, 0)}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* CTA Button Desktop */}
          <a href="tel:+393939923354" className="nav-cta-desktop">
            <Phone size={18} />
            Chiama Ora
          </a>

          {/* Mobile Menu Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="mobile-nav">
            <div className="mobile-nav-links">
              {navigation.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`mobile-nav-link ${isActive(item.path) ? 'mobile-nav-link-active' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false);
                    window.scrollTo(0, 0);
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <a 
              href="tel:+393939923354" 
              className="btn-primary mobile-cta"
              onClick={() => setIsMenuOpen(false)}
            >
              <Phone size={18} />
              Chiama Ora
            </a>
          </div>
        )}
      </nav>

      <style jsx>{`
        .header-sisaline {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border-light);
          box-shadow: 0 2px 8px rgba(0, 75, 147, 0.08);
        }

        .nav-container {
          width: 100%;
        }

        .nav-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 2.5rem;
          gap: 3rem;
        }

        .logo-link {
          display: flex;
          align-items: center;
          gap: 1rem;
          text-decoration: none;
          transition: opacity 0.3s ease;
        }

        .logo-link:hover {
          opacity: 0.9;
        }

        .logo-image {
          width: 130px;
          height: 130px;
          object-fit: contain;
          margin: -15px 0;
        }

        .logo-text {
          display: flex;
          flex-direction: column;
          gap: 0.125rem;
          white-space: nowrap;
        }

        .logo-name {
          font-weight: 800;
          font-size: 1.5rem;
          color: var(--text-primary);
          letter-spacing: 0.02em;
          white-space: nowrap;
        }

        .logo-srl {
          font-size: 1rem;
          color: var(--text-primary);
          font-weight: 600;
        }

        .logo-tagline {
          font-size: 0.75rem;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.1em;
        }

        .nav-links-desktop {
          display: none;
          align-items: center;
          gap: 2rem;
          flex: 1;
          justify-content: center;
          margin-left: 7rem;
        }

        @media (min-width: 1024px) {
          .nav-links-desktop {
            display: flex;
          }
        }

        .nav-link {
          color: var(--text-primary);
          text-decoration: none;
          font-size: 0.95rem;
          font-weight: 500;
          padding: 0.5rem 0;
          position: relative;
          transition: color 0.3s ease;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: var(--brand-orange);
          transition: width 0.3s ease;
        }

        .nav-link:hover {
          color: var(--brand-orange);
        }

        .nav-link:hover::after,
        .nav-link-active::after {
          width: 100%;
        }

        .nav-link-active {
          color: var(--brand-orange);
        }

        .nav-cta-desktop {
          display: none;
        }

        @media (min-width: 1024px) {
          .nav-cta-desktop {
            display: inline-flex;
            background: var(--brand-red);
            color: white;
            border: none;
            border-radius: 10rem;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            font-size: 0.95rem;
            cursor: pointer;
            transition: all 0.3s ease;
            text-decoration: none;
            align-items: center;
            gap: 0.5rem;
            text-transform: uppercase;
            letter-spacing: 0.02em;
          }

          .nav-cta-desktop:hover {
            background: var(--brand-red-hover);
            transform: scale(1.02);
          }
        }

        .mobile-menu-button {
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          color: var(--text-primary);
          cursor: pointer;
          padding: 0.5rem;
          transition: color 0.3s ease;
        }

        .mobile-menu-button:hover {
          color: var(--brand-orange);
        }

        @media (min-width: 1024px) {
          .mobile-menu-button {
            display: none;
          }
        }

        .mobile-nav {
          display: flex;
          flex-direction: column;
          background: rgba(248, 249, 250, 0.98);
          border-top: 1px solid var(--border-light);
          padding: 1.5rem;
          gap: 1.5rem;
        }

        @media (min-width: 1024px) {
          .mobile-nav {
            display: none;
          }
        }

        .mobile-nav-links {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .mobile-nav-link {
          color: var(--text-primary);
          text-decoration: none;
          font-size: 1.125rem;
          font-weight: 500;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          transition: all 0.3s ease;
        }

        .mobile-nav-link:hover,
        .mobile-nav-link-active {
          background: var(--bg-card-hover);
          color: var(--brand-orange);
        }

        .mobile-cta {
          width: 100%;
          justify-content: center;
        }

        @media (max-width: 640px) {
          .nav-content {
            padding: 1rem 1.25rem;
          }

          .logo-image {
            width: 60px;
            height: 60px;
          }

          .logo-name {
            font-size: 1.125rem;
          }

          .logo-srl {
            font-size: 0.875rem;
          }

          .logo-tagline {
            font-size: 0.6875rem;
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
